import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Descriptions, Tag, Button, Space, Spin, message, Table, Statistic, Row, Col, DatePicker, Modal, Avatar, Empty,
} from 'antd';
import {
  ArrowLeftOutlined, UpOutlined, DownOutlined, FireOutlined, RiseOutlined, CalendarOutlined,
  SoundOutlined, UserOutlined, RobotOutlined, MessageOutlined, QuestionCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { getUserDetailFull, getUserSessions, getUserFoodLogs, VoiceSession, FoodLogResponse, FoodLogItemResponse } from '../../api/admin';
import { getSessionMessages, SessionMessage, FoodItem, FoodResult, UserResult, WeightResult, ExerciseResult, LLMAnalysisResult } from '../../api/sessions';
import type { UserDetail as UserDetailData } from '../../api/admin';

const roleMap: Record<string, { color: string; text: string }> = {
  super_admin: { color: 'red', text: '超级管理员' },
  admin: { color: 'blue', text: '管理员' },
  user: { color: 'default', text: '普通用户' },
};

const statusMap: Record<string, { color: string; text: string }> = {
  active: { color: 'green', text: '正常' },
  disabled: { color: 'orange', text: '禁用' },
  banned: { color: 'red', text: '封禁' },
};

const mealTypeMap: Record<string, { emoji: string; text: string; color: string }> = {
  breakfast: { emoji: '🌅', text: '早餐', color: 'orange' },
  lunch: { emoji: '☀️', text: '午餐', color: 'green' },
  dinner: { emoji: '🌙', text: '晚餐', color: 'blue' },
  snack: { emoji: '🍪', text: '零食', color: 'purple' },
};

const CollapsibleCard: React.FC<{
  title: React.ReactNode;
  defaultCollapsed?: boolean;
  children: React.ReactNode;
  style?: React.CSSProperties;
}> = ({ title, defaultCollapsed = false, children, style }) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  return (
    <Card
      title={title}
      extra={
        <Button
          type="text"
          size="small"
          icon={collapsed ? <DownOutlined /> : <UpOutlined />}
          onClick={() => setCollapsed(!collapsed)}
        />
      }
      styles={{ body: collapsed ? { display: 'none', padding: 0 } : undefined }}
      style={style}
    >
      {children}
    </Card>
  );
};

const macroLabel: Record<string, { label: string; color: string }> = {
  calories: { label: '卡路里', color: '#f5222d' },
  protein: { label: '蛋白质', color: '#52c41a' },
  fat: { label: '脂肪', color: '#fa8c16' },
  carbs: { label: '碳水', color: '#1890ff' },
};

const FoodLogCard: React.FC<{ log: FoodLogResponse }> = ({ log }) => {
  const mealInfo = mealTypeMap[log.mealType ?? ''] ?? { emoji: '🍽️', text: log.mealType ?? '未知', color: 'default' };

  return (
    <Card
      size="small"
      style={{ marginBottom: 8, borderRadius: 8 }}
      styles={{ body: { padding: '10px 14px' } }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <Space>
          <span style={{ fontSize: 16 }}>{mealInfo.emoji}</span>
          <Tag color={mealInfo.color} style={{ marginRight: 0 }}>{mealInfo.text}</Tag>
        </Space>
        <span style={{ fontWeight: 600, fontSize: 15, color: '#f5222d' }}>
          {log.totalCalories != null ? `${log.totalCalories.toFixed(0)} kcal` : '-'}
        </span>
      </div>
      {log.originalText && (
        <div style={{ fontSize: 12, color: '#999', marginBottom: 6, fontStyle: 'italic' }}>
          "{log.originalText}"
        </div>
      )}
      {log.items && log.items.length > 0 && (
        <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 6 }}>
          {log.items.map((item, idx) => (
            <FoodItemRow key={item.id ?? idx} item={item} />
          ))}
        </div>
      )}
      {(log.totalProtein != null || log.totalFat != null || log.totalCarbs != null) && (
        <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 6, marginTop: 4, display: 'flex', gap: 12, fontSize: 12, color: '#666' }}>
          {log.totalProtein != null && <span style={{ color: macroLabel.protein.color }}>蛋白质 {log.totalProtein.toFixed(1)}g</span>}
          {log.totalFat != null && <span style={{ color: macroLabel.fat.color }}>脂肪 {log.totalFat.toFixed(1)}g</span>}
          {log.totalCarbs != null && <span style={{ color: macroLabel.carbs.color }}>碳水 {log.totalCarbs.toFixed(1)}g</span>}
        </div>
      )}
    </Card>
  );
};

const FoodItemRow: React.FC<{ item: FoodLogItemResponse }> = ({ item }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 0' }}>
    <Space size={4}>
      <span style={{ fontSize: 13, color: '#333' }}>{item.name}</span>
      {item.amountText && <span style={{ fontSize: 12, color: '#999' }}>{item.amountText}</span>}
      {item.estimatedGrams != null && <span style={{ fontSize: 12, color: '#999' }}>({item.estimatedGrams.toFixed(0)}g)</span>}
      {item.isUserEdited && <Tag color="processing" style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px' }}>已修正</Tag>}
    </Space>
    <Space size={8} style={{ fontSize: 12 }}>
      {item.calories != null && <span style={{ color: macroLabel.calories.color }}>{item.calories.toFixed(0)} kcal</span>}
      {item.protein != null && <span style={{ color: macroLabel.protein.color }}>P {item.protein.toFixed(1)}</span>}
      {item.fat != null && <span style={{ color: macroLabel.fat.color }}>F {item.fat.toFixed(1)}</span>}
      {item.carbs != null && <span style={{ color: macroLabel.carbs.color }}>C {item.carbs.toFixed(1)}</span>}
      {item.confidence != null && (
        <span style={{ color: item.confidence >= 0.8 ? '#52c41a' : '#faad14', fontSize: 11 }}>
          {item.confidence >= 0.8 ? '高置信' : '低置信'}
        </span>
      )}
    </Space>
  </div>
);

const AnalysisFoodItemRow: React.FC<{ food: FoodItem }> = ({ food }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
    <Space size={4}>
      <FireOutlined style={{ color: '#fa8c16', fontSize: 12 }} />
      <span style={{ fontSize: 13, color: '#333' }}>{food.name}</span>
      {food.estimatedGrams > 0 && <span style={{ fontSize: 12, color: '#999' }}>{food.estimatedGrams.toFixed(0)}g</span>}
      {food.confidence > 0 && (
        <span style={{
          fontSize: 10, color: '#fff', borderRadius: 4, padding: '0 5px',
          lineHeight: '18px', background: food.confidence >= 0.8 ? '#52c41a' : '#faad14',
        }}>
          {(food.confidence * 100).toFixed(0)}%
        </span>
      )}
    </Space>
    <Space size={10} style={{ fontSize: 12 }}>
      <span style={{ color: '#f5222d' }}>{food.calories.toFixed(0)} kcal</span>
      {food.protein > 0 && <span style={{ color: '#52c41a' }}>P {food.protein.toFixed(1)}</span>}
      {food.fat > 0 && <span style={{ color: '#fa8c16' }}>F {food.fat.toFixed(1)}</span>}
      {food.carbs > 0 && <span style={{ color: '#1890ff' }}>C {food.carbs.toFixed(1)}</span>}
    </Space>
  </div>
);

const mealTypeLabel: Record<string, { label: string; color: string }> = {
  breakfast: { label: '早餐', color: 'orange' },
  lunch: { label: '午餐', color: 'green' },
  dinner: { label: '晚餐', color: 'blue' },
  snack: { label: '零食', color: 'purple' },
};

const InlineAnalysisCard: React.FC<{
  foodResult: FoodResult;
  isPartial: boolean;
  adopted: boolean;
}> = ({ foodResult, isPartial, adopted }) => {
  const mealInfo = mealTypeLabel[foodResult.mealType] ?? { label: foodResult.mealType || '未知', color: 'default' };

  return (
    <div style={{
      marginTop: 8,
      background: adopted ? '#f6ffed' : '#fffbe6',
      border: `1px solid ${adopted ? '#b7eb8f' : '#ffe58f'}`,
      borderRadius: 12,
      padding: '12px 14px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <Space size={6}>
          <Tag color={mealInfo.color} style={{ fontSize: 12, padding: '0 8px', lineHeight: '22px', margin: 0 }}>{mealInfo.label}</Tag>
          <Tag color={adopted ? 'success' : 'default'} style={{ fontSize: 11, padding: '0 6px', lineHeight: '20px', margin: 0 }}>
            {adopted ? '已采纳' : '已覆盖'}
          </Tag>
          {isPartial && <Tag color="processing" style={{ fontSize: 11, padding: '0 6px', lineHeight: '20px', margin: 0 }}>待补充</Tag>}
        </Space>
        <Space size={8}>
          <span style={{ fontWeight: 700, fontSize: 17, color: '#f5222d' }}>
            {foodResult.totalCalories.toFixed(0)} kcal
          </span>
          {foodResult.overallConfidence > 0 && (
            <span style={{
              fontSize: 11, color: foodResult.overallConfidence >= 0.8 ? '#52c41a' : '#faad14',
              background: foodResult.overallConfidence >= 0.8 ? '#f6ffed' : '#fffbe6',
              padding: '0 6px', borderRadius: 4, lineHeight: '20px',
            }}>
              {(foodResult.overallConfidence * 100).toFixed(0)}%
            </span>
          )}
        </Space>
      </div>

      {foodResult.foods.length > 0 && (
        <div style={{ borderTop: `1px solid ${adopted ? '#b7eb8f' : '#ffe58f'}`, paddingTop: 6 }}>
          {foodResult.foods.map((food, idx) => (
            <AnalysisFoodItemRow key={idx} food={food} />
          ))}
        </div>
      )}

      {foodResult.totalProtein > 0 || foodResult.totalFat > 0 || foodResult.totalCarbs > 0 ? (
        <div style={{
          borderTop: `1px solid ${adopted ? '#b7eb8f' : '#ffe58f'}`, paddingTop: 6, marginTop: 6,
          display: 'flex', gap: 14, fontSize: 12, color: '#666',
        }}>
          {foodResult.totalProtein > 0 && <span style={{ color: '#52c41a' }}>蛋白质 {foodResult.totalProtein.toFixed(1)}g</span>}
          {foodResult.totalFat > 0 && <span style={{ color: '#fa8c16' }}>脂肪 {foodResult.totalFat.toFixed(1)}g</span>}
          {foodResult.totalCarbs > 0 && <span style={{ color: '#1890ff' }}>碳水 {foodResult.totalCarbs.toFixed(1)}g</span>}
        </div>
      ) : null}
    </div>
  );
};

const genderLabel: Record<string, string> = {
  male: '男',
  female: '女',
};

const activityLabel: Record<string, string> = {
  sedentary: '久坐',
  light: '轻度运动',
  moderate: '中度运动',
  active: '活跃',
  very_active: '非常活跃',
};

const exerciseTypeLabel: Record<string, string> = {
  cardio: '有氧运动',
  strength: '力量训练',
  flexibility: '柔韧性训练',
  sports: '运动',
  other: '其他',
};

const exerciseIntensityLabel: Record<string, string> = {
  low: '低强度',
  medium: '中等强度',
  high: '高强度',
};

const UserResultCard: React.FC<{ userResult: UserResult }> = ({ userResult }) => (
  <div style={{
    marginTop: 8,
    background: '#e6f7ff',
    border: '1px solid #91d5ff',
    borderRadius: 12,
    padding: '12px 14px',
  }}>
    <div style={{ marginBottom: 8 }}>
      <Tag color="blue" style={{ fontSize: 12, padding: '0 8px', lineHeight: '22px', margin: 0 }}>
        用户信息识别
      </Tag>
      {userResult.isComplete === false && (
        <Tag color="processing" style={{ fontSize: 11, padding: '0 6px', lineHeight: '20px', marginLeft: 6 }}>
          待补充
        </Tag>
      )}
    </div>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', fontSize: 13, color: '#333', lineHeight: '24px' }}>
      {userResult.age != null && <span>年龄: <b>{userResult.age}</b> 岁</span>}
      {userResult.heightCm != null && <span>身高: <b>{userResult.heightCm}</b> cm</span>}
      {userResult.weightKg != null && <span>体重: <b>{userResult.weightKg}</b> kg</span>}
      {userResult.gender && <span>性别: <b>{genderLabel[userResult.gender] || userResult.gender}</b></span>}
      {userResult.activityLevel && <span>活动水平: <b>{activityLabel[userResult.activityLevel] || userResult.activityLevel}</b></span>}
    </div>
    {(userResult.targetWeightKg != null || userResult.dailyCalorieTarget != null ||
      userResult.proteinTarget != null || userResult.fatTarget != null || userResult.carbsTarget != null) && (
      <div style={{
        borderTop: '1px solid #91d5ff', paddingTop: 6, marginTop: 6,
        display: 'flex', flexWrap: 'wrap', gap: '6px 16px', fontSize: 13, color: '#333', lineHeight: '24px',
      }}>
        {userResult.targetWeightKg != null && <span>目标体重: <b>{userResult.targetWeightKg}</b> kg</span>}
        {userResult.dailyCalorieTarget != null && <span>每日热量目标: <b>{userResult.dailyCalorieTarget.toFixed(0)}</b> kcal</span>}
        {userResult.proteinTarget != null && <span>蛋白质目标: <b>{userResult.proteinTarget.toFixed(1)}</b> g</span>}
        {userResult.fatTarget != null && <span>脂肪目标: <b>{userResult.fatTarget.toFixed(1)}</b> g</span>}
        {userResult.carbsTarget != null && <span>碳水目标: <b>{userResult.carbsTarget.toFixed(1)}</b> g</span>}
      </div>
    )}
  </div>
);

const ExerciseResultCard: React.FC<{ exerciseResult: ExerciseResult }> = ({ exerciseResult }) => (
  <div style={{
    marginTop: 8,
    background: '#fff7e6',
    border: '1px solid #ffd591',
    borderRadius: 12,
    padding: '12px 14px',
  }}>
    <div style={{ marginBottom: 8 }}>
      <Tag color="orange" style={{ fontSize: 12, padding: '0 8px', lineHeight: '22px', margin: 0 }}>
        运动识别
      </Tag>
    </div>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', fontSize: 13, color: '#333', lineHeight: '24px' }}>
      {exerciseResult.type && <span>类型: <b>{exerciseTypeLabel[exerciseResult.type] || exerciseResult.type}</b></span>}
      {exerciseResult.name && <span>名称: <b>{exerciseResult.name}</b></span>}
      {exerciseResult.durationMinutes != null && <span>时长: <b>{exerciseResult.durationMinutes}</b> 分钟</span>}
      {exerciseResult.distanceKm != null && <span>距离: <b>{exerciseResult.distanceKm.toFixed(1)}</b> km</span>}
      {exerciseResult.caloriesBurned != null && <span>消耗热量: <b>{exerciseResult.caloriesBurned.toFixed(0)}</b> kcal</span>}
      {exerciseResult.countValue != null && <span>次数: <b>{exerciseResult.countValue}</b>{exerciseResult.countUnit || ''}</span>}
      {exerciseResult.intensity && <span>强度: <b>{exerciseIntensityLabel[exerciseResult.intensity] || exerciseResult.intensity}</b></span>}
      {exerciseResult.date && <span>日期: <b>{exerciseResult.date}</b></span>}
      {exerciseResult.note && <span>备注: <b>{exerciseResult.note}</b></span>}
    </div>
  </div>
);

const WeightResultCard: React.FC<{ weightResult: WeightResult }> = ({ weightResult }) => (
  <div style={{
    marginTop: 8,
    background: '#f9f0ff',
    border: '1px solid #d3adf7',
    borderRadius: 12,
    padding: '12px 14px',
  }}>
    <div style={{ marginBottom: 8 }}>
      <Tag color="purple" style={{ fontSize: 12, padding: '0 8px', lineHeight: '22px', margin: 0 }}>
        体重识别
      </Tag>
    </div>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', fontSize: 13, color: '#333', lineHeight: '24px' }}>
      {weightResult.weightKg != null && (
        <span>体重: <b style={{ fontSize: 16, color: '#722ed1' }}>{weightResult.weightKg}</b> kg</span>
      )}
      {weightResult.date && <span>日期: <b>{weightResult.date}</b></span>}
      {weightResult.note && <span>备注: <b>{weightResult.note}</b></span>}
    </div>
  </div>
);

const SessionDetailModal: React.FC<{
  open: boolean;
  sessionId: string;
  onClose: () => void;
}> = ({ open, sessionId, onClose }) => {
  const [messages, setMessages] = useState<SessionMessage[]>([]);
  const [sessionResult, setSessionResult] = useState<LLMAnalysisResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [expandedResults, setExpandedResults] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (open && sessionId) {
      setLoading(true);
      getSessionMessages(Number(sessionId))
        .then((res) => {
          setMessages(res.messages);
          setSessionResult(res.result);
        })
        .catch(() => message.error('获取会话消息失败'))
        .finally(() => setLoading(false));
    } else {
      setMessages([]);
      setSessionResult(null);
      setExpandedResults(new Set());
    }
  }, [open, sessionId]);

  const toggleResultExpand = (msgId: number) => {
    setExpandedResults(prev => {
      const next = new Set(prev);
      if (next.has(msgId)) {
        next.delete(msgId);
      } else {
        next.add(msgId);
      }
      return next;
    });
  };

  const hasAnyResult = (result: LLMAnalysisResult | null | undefined) => {
    if (!result) return false;
    const hasFoodResult = result.foodResult && result.foodResult.foods && result.foodResult.foods.length > 0;
    const hasUserResult = result.userResult && (result.userResult.age != null || result.userResult.gender != null || result.userResult.heightCm != null || result.userResult.weightKg != null);
    const hasWeightResult = result.weightResult && result.weightResult.weightKg != null;
    const hasExerciseResult = result.exerciseResult && (result.exerciseResult.name != null || result.exerciseResult.type != null);
    return !!(hasFoodResult || hasUserResult || hasWeightResult || hasExerciseResult);
  };

  const renderResultCard = (result: LLMAnalysisResult) => {
    const hasFoodData = result.foodResult && result.foodResult.foods && result.foodResult.foods.length > 0;
    const hasUserData = result.userResult && (result.userResult.age != null || result.userResult.gender != null || result.userResult.heightCm != null || result.userResult.weightKg != null);
    const hasWeightData = result.weightResult && result.weightResult.weightKg != null;
    const hasExerciseData = result.exerciseResult && (result.exerciseResult.name != null || result.exerciseResult.type != null);
    return (
      <div style={{ marginLeft: 46, padding: '8px 0' }}>
        {hasFoodData && (
          <InlineAnalysisCard
            foodResult={result.foodResult!}
            isPartial={result.isPartial}
            adopted={false}
          />
        )}
        {hasUserData && <UserResultCard userResult={result.userResult!} />}
        {hasExerciseData && <ExerciseResultCard exerciseResult={result.exerciseResult!} />}
        {hasWeightData && <WeightResultCard weightResult={result.weightResult!} />}
      </div>
    );
  };

  return (
    <Modal
      title="会话详情"
      open={open}
      onCancel={onClose}
      footer={null}
      width={760}
      styles={{ body: { padding: '12px 24px', maxHeight: '70vh', overflow: 'auto' } }}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0' }}><Spin /></div>
      ) : messages.length === 0 ? (
        <Empty description="暂无消息" />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {messages.map((msg, idx) => {
            const isUser = msg.role === 'user';
            const isExpanded = expandedResults.has(msg.id);
            const canExpand = !isUser && hasAnyResult(msg.result);
            return (
              <React.Fragment key={msg.id}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    flexDirection: isUser ? 'row-reverse' : 'row',
                  }}
                >
                  <Avatar
                    size={36}
                    icon={isUser ? <UserOutlined /> : <RobotOutlined />}
                    style={{ flexShrink: 0, backgroundColor: isUser ? '#1890ff' : '#52c41a' }}
                  />
                  <div style={{ maxWidth: '70%', position: 'relative' }}>
                    {canExpand && (
                      <QuestionCircleOutlined
                        onClick={() => toggleResultExpand(msg.id)}
                        style={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          fontSize: 18,
                          color: '#faad14',
                          cursor: 'pointer',
                          zIndex: 1,
                          opacity: isExpanded ? 1 : 0.7,
                        }}
                      />
                    )}
                    <div
                      style={{
                        padding: '10px 14px',
                        borderRadius: 12,
                        fontSize: 14,
                        lineHeight: 1.6,
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap',
                        backgroundColor: isUser ? '#e6f7ff' : '#f6f6f6',
                        color: '#333',
                        borderTopLeftRadius: isUser ? 12 : 4,
                        borderTopRightRadius: isUser ? 4 : 12,
                      }}
                    >
                      {msg.content || '(空)'}
                    </div>
                  </div>
                </div>
                {isExpanded && !isUser && msg.result && renderResultCard(msg.result)}
              </React.Fragment>
            );
          })}
          {sessionResult && hasAnyResult(sessionResult) && (
            <div style={{ marginTop: 8, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
              <div style={{ fontSize: 13, color: '#999', marginBottom: 8 }}>最终识别结果</div>
              {sessionResult.foodResult && sessionResult.foodResult.foods && sessionResult.foodResult.foods.length > 0 && (
                <InlineAnalysisCard
                  foodResult={sessionResult.foodResult}
                  isPartial={sessionResult.isPartial}
                  adopted={true}
                />
              )}
              {sessionResult.userResult && (sessionResult.userResult.age != null || sessionResult.userResult.gender != null || sessionResult.userResult.heightCm != null || sessionResult.userResult.weightKg != null) && (
                <UserResultCard userResult={sessionResult.userResult} />
              )}
              {sessionResult.weightResult && sessionResult.weightResult.weightKg != null && (
                <WeightResultCard weightResult={sessionResult.weightResult} />
              )}
              {sessionResult.exerciseResult && (sessionResult.exerciseResult.name != null || sessionResult.exerciseResult.type != null) && (
                <ExerciseResultCard exerciseResult={sessionResult.exerciseResult} />
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

const UserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<UserDetailData | null>(null);
  const [sessions, setSessions] = useState<VoiceSession[]>([]);
  const [sessionTotal, setSessionTotal] = useState(0);
  const [sessionPage, setSessionPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [sessionLoading, setSessionLoading] = useState(false);

  const [foodDate, setFoodDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [foodLogs, setFoodLogs] = useState<FoodLogResponse[]>([]);
  const [foodLoading, setFoodLoading] = useState(false);

  const [sessionModalOpen, setSessionModalOpen] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      getUserDetailFull(id)
        .then(setDetail)
        .catch(() => message.error('获取用户详情失败'))
        .finally(() => setLoading(false));
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;
    setFoodLoading(true);
    getUserFoodLogs(id, { date: foodDate, page: 1, pageSize: 50 })
      .then((res) => setFoodLogs(res.items || []))
      .catch(() => message.error('获取饮食记录失败'))
      .finally(() => setFoodLoading(false));
  }, [id, foodDate]);

  const loadSessions = useCallback((page: number) => {
    if (!id) return;
    setSessionLoading(true);
    getUserSessions(id, { page, pageSize: 10 })
      .then((res) => {
        setSessions(res.items || []);
        setSessionTotal(res.total);
        setSessionPage(page);
      })
      .catch(() => message.error('获取会话历史失败'))
      .finally(() => setSessionLoading(false));
  }, [id]);

  useEffect(() => {
    loadSessions(1);
  }, [loadSessions]);

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  }

  if (!detail) {
    return <div>用户不存在</div>;
  }

  const role = roleMap[detail.role] || { color: 'default', text: detail.role };
  const status = statusMap[detail.status] || { color: 'default', text: detail.status };

  const fmtCal = (v: number | null | undefined) =>
    v != null ? `${v.toFixed(1)} kcal` : '-';

  const sessionColumns: ColumnsType<VoiceSession> = [
    { title: 'ID', dataIndex: 'id', width: 100 },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (s: string) => {
        const m: Record<string, { color: string; text: string }> = {
          completed: { color: 'green', text: '已完成' },
          processing: { color: 'blue', text: '处理中' },
          recording: { color: 'orange', text: '录音中' },
          waiting_clarification: { color: 'purple', text: '等待澄清' },
          failed: { color: 'red', text: '失败' },
        };
        const v = m[s] || { color: 'default', text: s };
        return <Tag color={v.color}>{v.text}</Tag>;
      },
    },
    { title: '对话轮次', dataIndex: 'clarificationRound', width: 100 },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      width: 180,
      render: (t: number) => (t ? dayjs(t * 1000).format('YYYY-MM-DD HH:mm:ss') : '-'),
    },
    {
      title: '操作',
      width: 120,
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<MessageOutlined />}
          onClick={() => {
            setSelectedSessionId(record.id);
            setSessionModalOpen(true);
          }}
        >
          查看详情
        </Button>
      ),
    },
  ];

  return (
    <div style={{ height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <Space style={{ padding: '16px 24px', flexShrink: 0 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/users')}>返回</Button>
      </Space>

      <div style={{ flex: 1, overflow: 'auto', padding: '0 24px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <CollapsibleCard title={<span><UserOutlined style={{ marginRight: 8 }} />用户基础信息</span>}>
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label="ID">{detail.id}</Descriptions.Item>
          <Descriptions.Item label="昵称">{detail.nickname}</Descriptions.Item>
          <Descriptions.Item label="邮箱">{detail.email}</Descriptions.Item>
          <Descriptions.Item label="手机号">{detail.phone}</Descriptions.Item>
          <Descriptions.Item label="角色"><Tag color={role.color}>{role.text}</Tag></Descriptions.Item>
          <Descriptions.Item label="状态"><Tag color={status.color}>{status.text}</Tag></Descriptions.Item>
          <Descriptions.Item label="最后登录时间">
            {detail.lastLoginAt ? dayjs(Number(detail.lastLoginAt) * 1000).format('YYYY-MM-DD HH:mm:ss') : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="创建时间">
            {dayjs(Number(detail.createdAt) * 1000).format('YYYY-MM-DD HH:mm:ss')}
          </Descriptions.Item>
          <Descriptions.Item label="上一次会话时间">
            {detail.lastSessionAt ? dayjs(Number(detail.lastSessionAt) * 1000).format('YYYY-MM-DD HH:mm:ss') : '无会话'}
          </Descriptions.Item>
        </Descriptions>
      </CollapsibleCard>

      <CollapsibleCard title={<span><RiseOutlined style={{ marginRight: 8 }} />身体指标与营养目标</span>}>
        {detail.profile ? (
          <div>
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col span={6}>
                <Statistic title="身高" value={detail.profile.heightCm != null ? `${detail.profile.heightCm} cm` : '-'} />
              </Col>
              <Col span={6}>
                <Statistic title="体重" value={detail.profile.weightKg != null ? `${detail.profile.weightKg} kg` : '-'} />
              </Col>
              <Col span={6}>
                <Statistic title="年龄" value={detail.profile.age != null ? `${detail.profile.age} 岁` : '-'} />
              </Col>
              <Col span={6}>
                <Statistic title="性别" value={detail.profile.gender === 'male' ? '男' : detail.profile.gender === 'female' ? '女' : '-'} />
              </Col>
            </Row>
            <Descriptions column={3} bordered size="small">
              <Descriptions.Item label="目标体重">{detail.profile.targetWeightKg != null ? `${detail.profile.targetWeightKg} kg` : '-'}</Descriptions.Item>
              <Descriptions.Item label="活动水平">
                {detail.profile.activityLevel
                  ? ({ sedentary: '久坐', light: '轻度', moderate: '中度', active: '活跃', very_active: '非常活跃' } as Record<string, string>)[detail.profile.activityLevel] || detail.profile.activityLevel
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="每日卡路里目标">{fmtCal(detail.profile.dailyCalorieTarget)}</Descriptions.Item>
              <Descriptions.Item label="蛋白质目标">{detail.profile.proteinTarget != null ? `${detail.profile.proteinTarget.toFixed(1)} g` : '-'}</Descriptions.Item>
              <Descriptions.Item label="脂肪目标">{detail.profile.fatTarget != null ? `${detail.profile.fatTarget.toFixed(1)} g` : '-'}</Descriptions.Item>
              <Descriptions.Item label="碳水目标">{detail.profile.carbsTarget != null ? `${detail.profile.carbsTarget.toFixed(1)} g` : '-'}</Descriptions.Item>
            </Descriptions>
          </div>
        ) : (
          <div style={{ color: '#999' }}>暂无身体指标数据</div>
        )}
        {detail.calorieStats && (
          <>
            <div style={{ height: 16 }} />
            <Row gutter={[16, 16]}>
              <Col span={6}>
                <Statistic title="总记录数" value={detail.calorieStats.totalLogs} prefix={<CalendarOutlined />} />
              </Col>
              <Col span={6}>
                <Statistic title="总卡路里" value={detail.calorieStats.totalCalories ?? 0} precision={1} suffix="kcal" prefix={<FireOutlined />} />
              </Col>
              <Col span={6}>
                <Statistic title="日均卡路里" value={detail.calorieStats.avgDailyCalories ?? 0} precision={1} suffix="kcal" prefix={<RiseOutlined />} />
              </Col>
              <Col span={6}>
                <Statistic title="今日摄入" value={detail.calorieStats.todayCalories ?? 0} precision={1} suffix="kcal" prefix={<FireOutlined />} />
              </Col>
            </Row>
          </>
        )}
      </CollapsibleCard>

      <CollapsibleCard
        title={<span><CalendarOutlined style={{ marginRight: 8 }} />饮食记录</span>}
      >
        <div style={{ marginBottom: 12 }}>
          <DatePicker
            value={dayjs(foodDate)}
            onChange={(d) => { if (d) setFoodDate(d.format('YYYY-MM-DD')); }}
            allowClear={false}
            style={{ width: 200 }}
          />
        </div>
        {foodLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}><Spin /></div>
        ) : foodLogs.length === 0 ? (
          <Empty description="该日期暂无饮食记录" />
        ) : (
          <div>
            {foodLogs.map((log) => (
              <FoodLogCard key={log.id} log={log} />
            ))}
            {(() => {
              const totalCals = foodLogs.reduce((s, l) => s + (l.totalCalories ?? 0), 0);
              const totalPro = foodLogs.reduce((s, l) => s + (l.totalProtein ?? 0), 0);
              const totalFat = foodLogs.reduce((s, l) => s + (l.totalFat ?? 0), 0);
              const totalCarbs = foodLogs.reduce((s, l) => s + (l.totalCarbs ?? 0), 0);
              return (
                <Card size="small" styles={{ body: { padding: '10px 14px', background: '#fafafa' } }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 600 }}>当日总计</span>
                    <span style={{ fontWeight: 600, fontSize: 16, color: '#f5222d' }}>{totalCals.toFixed(0)} kcal</span>
                  </div>
                  <div style={{ display: 'flex', gap: 16, marginTop: 4, fontSize: 13, color: '#666' }}>
                    {totalPro > 0 && <span style={{ color: macroLabel.protein.color }}>蛋白质 {totalPro.toFixed(1)}g</span>}
                    {totalFat > 0 && <span style={{ color: macroLabel.fat.color }}>脂肪 {totalFat.toFixed(1)}g</span>}
                    {totalCarbs > 0 && <span style={{ color: macroLabel.carbs.color }}>碳水 {totalCarbs.toFixed(1)}g</span>}
                  </div>
                </Card>
              );
            })()}
          </div>
        )}
      </CollapsibleCard>

      <CollapsibleCard title={<span><SoundOutlined style={{ marginRight: 8 }} />语音会话历史</span>}>
        <Table
          columns={sessionColumns}
          dataSource={sessions}
          rowKey="id"
          loading={sessionLoading}
          scroll={{ y: 300 }}
          pagination={{
            current: sessionPage,
            pageSize: 10,
            total: sessionTotal,
            onChange: loadSessions,
            showSizeChanger: false,
          }}
          size="small"
        />
      </CollapsibleCard>
      </div>

      <SessionDetailModal
        open={sessionModalOpen}
        sessionId={selectedSessionId ?? ''}
        onClose={() => {
          setSessionModalOpen(false);
          setSelectedSessionId(null);
        }}
      />
    </div>
  );
};

export default UserDetail;
